import { useForm, useFormState } from "react-hook-form";

import { CashbackSettingsFields } from "../../../../components/CashbackSettingsFields";
import { FormInput, InputDate } from "../../../../components/Input";
import {
  Field,
  FieldHelper,
  FieldLabel,
  FormCard,
  FormPanel,
  LabelRow,
  discardCopyOf,
} from "../../../../components/FormPanel";
import {
  buildDraftDetails,
  ConfirmDetail,
  joinDetailParts,
} from "../../../../components/PopUp/confirmShared";
import { DateFormat, formatDate } from "../../../../utils";
import { MobileSavingsForm } from "./MobileSavingsForm";
import { DEFAULT_SAVING, FORM_ID } from "./constants";
import { SavingFormValues, SavingsPanelProps } from "./types";

export const SavingsPanel = ({
  saving,
  isOpen,
  onClose,
  handleOnSubmit,
}: SavingsPanelProps) => {
  const resetValues = saving ?? DEFAULT_SAVING;

  const form = useForm<SavingFormValues>({
    defaultValues: resetValues,
    mode: "onBlur",
    reValidateMode: "onChange",
  });
  const { control, getValues, watch } = form;

  const { isSubmitting } = useFormState({ control });

  const isEdit = Boolean(saving);

  const watchedName = watch("name");
  const eyebrow = isEdit ? "EDIT SAVING" : "NEW SAVING";
  const desktopTitle = isEdit ? "Edit saving" : "Create saving";
  const panelTitle = watchedName || desktopTitle;
  const desktopActionLabel = isEdit ? "Save changes" : "Create saving";
  const mobileTitle = isEdit ? "Edit saving" : "New saving";
  const mobileActionLabel = isEdit ? "Save" : "Create";

  const buildDiscardDetails = (): ConfirmDetail => {
    const values = getValues();

    if (isEdit) {
      return {
        pill: { label: "SAVING", variant: "DEFAULT" },
        title: values.name || saving?.name || "Saving",
        subtitle: joinDetailParts(
          values.institution,
          formatDate(values.startDate, DateFormat.DMY_SPACE),
        ),
      };
    }

    return buildDraftDetails({
      noun: "saving",
      fieldValues: [values.name, values.institution, values.startDate],
      primaryLabel: values.name,
    });
  };

  const discard = {
    ...discardCopyOf(isEdit, "saving"),
    details: buildDiscardDetails,
  };

  return (
    <FormPanel
      open={isOpen}
      onClose={onClose}
      ariaLabel="Savings account form"
      eyebrow={eyebrow}
      title={panelTitle}
      form={form}
      resetValues={resetValues}
      formId={FORM_ID}
      onSubmit={handleOnSubmit}
      submitLabel={desktopActionLabel}
      discard={discard}
      mobile={{
        title: mobileTitle,
        submitLabel: mobileActionLabel,
        fields: (
          <MobileSavingsForm control={control} isSubmitting={isSubmitting} />
        ),
      }}
    >
      <FormCard>
        <Field>
          <LabelRow>
            <FieldLabel htmlFor="saving-name">Name *</FieldLabel>
          </LabelRow>
          <FormInput
            id="saving-name"
            required
            rules={{
              required: "Name is required!",
              minLength: {
                value: 3,
                message: "Name should have more than 3 letters!",
              },
            }}
            name="name"
            control={control}
            handleClear={false}
            disabled={isSubmitting}
            placeholder="e.g. Emergency Fund"
            autoFocus={true}
          />
        </Field>
      </FormCard>

      <FormCard>
        <Field>
          <LabelRow>
            <FieldLabel htmlFor="saving-institution">Institution</FieldLabel>
          </LabelRow>
          <FormInput
            id="saving-institution"
            name="institution"
            control={control}
            handleClear={false}
            disabled={isSubmitting}
            placeholder="e.g. Revolut"
          />
          <FieldHelper>The bank or provider holding this saving.</FieldHelper>
        </Field>

        <Field>
          <LabelRow>
            <FieldLabel htmlFor="saving-start-date">Start date *</FieldLabel>
          </LabelRow>
          <InputDate
            id="saving-start-date"
            rules={{ required: "Start date is required!" }}
            name="startDate"
            control={control}
            disabled={isSubmitting}
            maxDate={new Date()}
            placeholder="YYYY/MM/DD"
          />
          <FieldHelper>When you opened this saving.</FieldHelper>
        </Field>
      </FormCard>
      <CashbackSettingsFields control={control} disabled={isSubmitting} />
    </FormPanel>
  );
};
